import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API, formatApiError } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { 
  Plus, 
  ArrowRight,
  Search,
  MoreHorizontal,
  Trash2,
  LogOut,
  Loader2,
  FolderOpen,
  Film
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    setCreating(true);
    try {
      const response = await axios.post(`${API}/projects`, {
        name: newProjectName,
        description: newProjectDescription
      });
      setProjects([response.data, ...projects]);
      setCreateDialogOpen(false);
      setNewProjectName("");
      setNewProjectDescription("");
      navigate(`/project/${response.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const filteredProjects = projects.filter(project => 
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      {/* Header */}
      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/dashboard" className="flex items-center">
              <img src="/assets/logo.png" alt="MasteringMate" className="h-7" />
            </Link>

            <div className="flex items-center gap-6">
              {user?.role === "admin" && (
                <button 
                  onClick={() => navigate("/admin")}
                  className="text-sm text-cyan-400/60 hover:text-cyan-300 transition-colors"
                  data-testid="admin-btn"
                >
                  Admin
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-3 text-sm" data-testid="user-menu-btn">
                    <span className="text-cyan-200/60">{user?.name}</span>
                    <div className="w-8 h-8 rounded-sm bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-300">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#020a14] border-cyan-500/20 rounded-sm">
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-cyan-200/60 hover:text-cyan-200 focus:text-cyan-200 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        {/* Title Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-['Rajdhani'] tracking-wide">PROJECTS</h1>
            <p className="text-cyan-200/40 mt-1">{projects.length} total</p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="h-12 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm"
                data-testid="new-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                NEW PROJECT
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#020a14] border-cyan-500/20 rounded-sm sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Rajdhani'] text-xl tracking-wide">NEW PROJECT</DialogTitle>
              </DialogHeader>
              <form onSubmit={createProject} className="space-y-6 mt-6">
                <div className="space-y-2">
                  <Label className="text-sm text-cyan-300/60">Project Name</Label>
                  <Input
                    placeholder="e.g., Brand X Summer Campaign"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="h-12 rounded-sm"
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-cyan-300/60">Description (optional)</Label>
                  <Input
                    placeholder="Brief description"
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    className="h-12 rounded-sm"
                    data-testid="project-description-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                  disabled={creating}
                  data-testid="create-project-submit"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "CREATE"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-500/30" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 pl-12 rounded-sm"
            data-testid="search-projects"
          />
        </div>

        {/* Projects */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400/40" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-24 card-glass rounded-sm">
            <FolderOpen className="w-10 h-10 mx-auto text-cyan-500/20 mb-4" />
            <p className="text-cyan-200/40">
              {projects.length === 0 ? "No projects yet" : "No projects match your search"}
            </p>
          </div>
        ) : (
          <div className="border-t border-cyan-500/10">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="group border-b border-cyan-500/10 py-4 flex items-center justify-between hover:bg-cyan-500/[0.03] transition-colors px-4 -mx-4 rounded-sm"
                data-testid={`project-card-${project.id}`}
              >
                <div 
                  className="flex-1 cursor-pointer"
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-cyan-500/30 font-mono w-8">{String(index + 1).padStart(2, '0')}</span>
                    {/* Thumbnail */}
                    <div className="w-20 h-12 rounded-sm overflow-hidden bg-cyan-500/5 border border-cyan-500/10 shrink-0">
                      {project.thumbnail ? (
                        <img
                          src={`${API}/projects/${project.id}/thumbnail`}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          data-testid={`project-thumb-${project.id}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-4 h-4 text-cyan-500/15" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-['Rajdhani'] text-lg tracking-wide group-hover:text-cyan-300 transition-colors">
                        {project.name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-cyan-200/30 mt-1">
                        <span className="capitalize">{project.status || "Draft"}</span>
                        <span>-</span>
                        <span>{new Date(project.created_at).toLocaleDateString()}</span>
                        {project.video_duration > 0 && (
                          <>
                            <span>-</span>
                            <span>{project.video_duration.toFixed(1)}s</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => navigate(`/project/${project.id}`)}
                    className="text-sm text-cyan-400/40 hover:text-cyan-300 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    Open <ArrowRight className="w-4 h-4" />
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 text-cyan-500/30 hover:text-cyan-300" data-testid={`project-menu-${project.id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#020a14] border-cyan-500/20 rounded-sm">
                      <DropdownMenuItem 
                        onClick={() => deleteProject(project.id)}
                        className="text-red-400 hover:text-red-300 focus:text-red-300 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
